import { CreditCard, Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  findPhysicalCardMethodForVirtualCard,
  type PaymentMethod,
  useAvailablePaymentMethods,
} from "@/features/payment-methods";
import {
  usePaymentMethods,
  type PaymentAccountCard,
} from "@/features/payments";

/** Set by the card that sent the participant here, so its own plastic is ordered. */
interface OrderPhysicalCardState {
  cardId?: string;
}

export const OrderPhysicalCardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { cards } = usePaymentMethods();
  const { availablePaymentMethods } = useAvailablePaymentMethods();

  // The order belongs to the programme of the virtual card it is placed from,
  // so a Stripe Virtual Stipend Card orders a Physical Stipend Card rather than
  // the Highnote Physical Card this page used to hard-wire. The card the
  // participant pressed the action on travels in the route state; without it
  // (a deep link, or a refresh) the first held card with plastic on offer is
  // the one being upgraded.
  const orderableMethodFor = (
    card: PaymentAccountCard
  ): PaymentMethod | undefined =>
    findPhysicalCardMethodForVirtualCard(
      availablePaymentMethods,
      card.card_type,
      card.provider
    );

  const sourceCardId = (state as OrderPhysicalCardState | null)?.cardId;
  const sourceCard = cards.find((card) => card.id === sourceCardId);
  const physicalCardMethod =
    (sourceCard && orderableMethodFor(sourceCard)) ??
    cards.map(orderableMethodFor).find((method) => method !== undefined);

  const handleContinue = () => {
    if (!physicalCardMethod) {
      return;
    }
    navigate(
      `/payment-methods/${physicalCardMethod.method_provider_name}/create`
    );
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center gap-4">
          <CreditCard className="size-5 text-primary" />
          <div>
            <CardTitle className="text-xl">
              {t("physicalCard_order_title")}
            </CardTitle>
            <CardDescription className="mt-1">
              {t("physicalCard_order_description")}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
          <div className="flex items-start gap-3">
            <Info className="size-5 text-blue-600 mt-0.5 shrink-0" />
            <div className="space-y-2 text-sm">
              <p className="font-medium text-blue-900">
                {t("physicalCard_info_different_number")}
              </p>
              <p className="text-blue-800">
                {t("physicalCard_info_virtual_access")}
              </p>
            </div>
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-3 mt-4">
          <h4 className="font-medium text-sm text-muted-foreground">
            {t("physicalCard_how_it_works")}
          </h4>
          <ol className="space-y-2 text-sm">
            <li className="flex gap-3">
              <span className="flex items-center justify-center size-6 rounded-full bg-primary/10 text-primary text-xs font-medium shrink-0">
                1
              </span>
              <span>{t("physicalCard_step_1")}</span>
            </li>
            <li className="flex gap-3">
              <span className="flex items-center justify-center size-6 rounded-full bg-primary/10 text-primary text-xs font-medium shrink-0">
                2
              </span>
              <span>{t("physicalCard_step_2")}</span>
            </li>
            <li className="flex gap-3">
              <span className="flex items-center justify-center size-6 rounded-full bg-primary/10 text-primary text-xs font-medium shrink-0">
                3
              </span>
              <span>{t("physicalCard_step_3")}</span>
            </li>
          </ol>
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-4">
          <Button variant="outline" className="flex-1" onClick={handleCancel}>
            {t("cancel")}
          </Button>

          <Button
            className="flex-1"
            onClick={handleContinue}
            disabled={!physicalCardMethod}
          >
            {t("continue_text")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
